"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Indicates if FormField's value or error has changed.
 * @param {FormField} ff1 - First FormField.
 * @param {FormField} ff2 - Second FormField.
 * @returns {boolean}
 */
function isFormFieldUpdated(ff1, ff2) {
    return ff1.val !== ff2.val || ff1.err !== ff2.err;
}
exports.isFormFieldUpdated = isFormFieldUpdated;
/**
 * Indicates if passed param is FormField object.
 * @param {any} field - Field to check.
 * @returns {boolean}
 */
function isFormField(field) {
    return field && typeof field.validators !== 'undefined';
}
exports.isFormField = isFormField;
/**
 * Creates FormField object.
 * @param {T} val - Initial value for FormField.
 * @param {Array<FormFieldValidatorFn<T> | FormFieldValidatorFnWithParamsObject<T>>} [validators] - Array of validators.
 * @param {boolean} [noValidate] - If true, FormField will not be validated.
 * @returns {FormField}
 */
function createFormField(val, validators, noValidate) {
    return {
        val: val,
        initialVal: val,
        err: '',
        validators: validators || [],
        noValidate: noValidate || false,
        touched: false,
    };
}
exports.createFormField = createFormField;
/**
 * Creates PwdField object.
 * @param {string} val - Initial value for PwdField.
 * @param {Array<FormFieldValidatorFn<string> | FormFieldValidatorFnWithParamsObject<string>>} [validators] - Array
 * of validators.
 * @param {boolean} [noValidate] - If true, FormField will not be validated.
 * @returns {PwdField}
 */
function createPasswordFormField(val, validators, noValidate) {
    return __assign(__assign({}, createFormField(val, validators, noValidate)), { peek: false });
}
exports.createPasswordFormField = createPasswordFormField;
/**
 * Converts all FormFields in passed object into {[key]: value}
 * @param {Object} form - Form.
 * @returns {Object}
 */
function formFieldsToValues(form) {
    return Object.keys(form).reduce(function (prev, key) {
        var _a, _b, _c;
        var field = form[key];
        if (typeof field !== 'object') {
            return __assign(__assign({}, prev), (_a = {}, _a[key] = field, _a));
        }
        return isFormField(field)
            ? // tslint:disable-next-line prefer-object-spread
                Object.assign({}, prev, (_b = {}, _b[key] = field.val, _b))
            : // tslint:disable-next-line prefer-object-spread
                Object.assign({}, prev, (_c = {}, _c[key] = formFieldsToValues(field), _c));
    }, {});
}
exports.formFieldsToValues = formFieldsToValues;
/**
 * Toggles noValidate on FormField.
 * @param {string} key - Key of FormField.
 * @param {FormField} field - FormField.
 * @param {Object} fieldsToSetValidation.
 * @returns {FormField}
 */
function setNoValidate(key, field, fieldsToSetValidation) {
    var shouldValidate = fieldsToSetValidation[key];
    return __assign(__assign({}, field), (typeof shouldValidate !== 'undefined' && { noValidate: !shouldValidate }));
}
exports.setNoValidate = setNoValidate;
/**
 * Toggles noValidate on Form
 * @param {Object} form - Form with FormFields.
 * @param {Object} fieldsToSetValidation.
 * @returns {Object}
 */
function changeFormFieldsValidation(form, fieldsToSetValidation) {
    return Object.keys(form).reduce(function (prev, key) {
        var _a, _b;
        var field = form[key];
        if (typeof field !== 'object') {
            return prev;
        }
        return isFormField(field)
            ? // tslint:disable-next-line prefer-object-spread
                Object.assign({}, prev, (_a = {}, _a[key] = setNoValidate(key, field, fieldsToSetValidation), _a))
            : // tslint:disable-next-line prefer-object-spread
                Object.assign({}, prev, (_b = {}, _b[key] = changeFormFieldsValidation(field, fieldsToSetValidation[key] || {}), _b));
    }, form);
}
exports.changeFormFieldsValidation = changeFormFieldsValidation;
/**
 * Changes FormField value.
 * @param {string} formToChange - Form.
 * @param {string} fieldName - Key of FormField in form.
 * @param {GenericTypeOfFormField<U>} value - Value to change
 * @returns {Object}
 */
function changeFormValue(formToChange, fieldName, value) {
    var fieldNameArr = typeof fieldName === 'string' ? fieldName.split('.') : [fieldName];
    var firstKey = fieldNameArr.shift();
    return Object.keys(formToChange).reduce(function (prev, key) {
        var _a, _b;
        var field = formToChange[key];
        if (key !== firstKey || typeof field !== 'object') {
            return prev;
        }
        return isFormField(field)
            ? // tslint:disable-next-line prefer-object-spread
                Object.assign({}, prev, (_a = {}, _a[key] = __assign(__assign({}, prev[key]), { val: value, err: '', touched: true }), _a))
            : // tslint:disable-next-line prefer-object-spread
                Object.assign({}, prev, (_b = {}, _b[key] = changeFormValue(field, fieldNameArr.join('.'), value), _b));
    }, formToChange);
}
exports.changeFormValue = changeFormValue;
/**
 * Indicated if any value in form is different than initial value.
 * @param {Object} formToCheck - Form.
 * @returns {boolean}
 */
function formHasChanged(formToCheck) {
    return Object.keys(formToCheck).some(function (key) {
        var field = formToCheck[key];
        if (typeof field !== 'object') {
            return false;
        }
        return isFormField(field) ? formFieldHasChanged(formToCheck[key]) : formHasChanged(field);
    });
}
exports.formHasChanged = formHasChanged;
/**
 * Indicated if FormField value is different than initial value.
 * @param {FormField} formField - FormField.
 * @returns {boolean}
 */
function formFieldHasChanged(formField) {
    return formField.val !== formField.initialVal;
}
exports.formFieldHasChanged = formFieldHasChanged;
/**
 * Indicated if form has error.
 * @param {Object} formToCheck - Form.
 * @returns {boolean}
 */
function formHasError(formToCheck) {
    return Object.keys(formToCheck).some(function (key) {
        var field = formToCheck[key];
        if (typeof field !== 'object') {
            return false;
        }
        return isFormField(field) ? formToCheck[key].err !== '' : formHasError(field);
    });
}
exports.formHasError = formHasError;
/**
 * Validates FormFields in form.
 * @param {Object} formToValidate - Form.
 * @returns {Object}
 */
function validateForm(formToValidate) {
    return Object.keys(formToValidate).reduce(function (prev, key) {
        var _a, _b;
        var field = formToValidate[key];
        if (typeof field !== 'object') {
            return prev;
        }
        return isFormField(field)
            ? // tslint:disable-next-line prefer-object-spread
                Object.assign({}, prev, (_a = {}, _a[key] = validateFormField(field), _a))
            : // tslint:disable-next-line prefer-object-spread
                Object.assign({}, prev, (_b = {}, _b[key] = validateForm(field), _b));
    }, formToValidate);
}
exports.validateForm = validateForm;
/**
 * Validates FormField.
 * @param {FormField} formField - FormField.
 * @returns {FormField}
 */
function validateFormField(formField) {
    var err = formField.noValidate
        ? ''
        : formField.validators.reduce(function (error, validator) {
            if (error) {
                return error;
            }
            return typeof validator === 'function'
                ? validator(formField.val)
                : validator.validator(validator.params())(formField.val);
        }, '');
    return __assign(__assign({}, formField), { err: err });
}
exports.validateFormField = validateFormField;
/**
 * Validates one specified FormField in form.
 * @param {Object} formToValidate - Form.
 * @param {string} fieldName - Key of FormField to validate.
 * @returns {Object}
 */
function validateOneFormField(formToValidate, fieldName) {
    var _a;
    var field = formToValidate[fieldName];
    if (typeof field !== 'object') {
        return formToValidate;
    }
    // tslint:disable-next-line prefer-object-spread
    return Object.assign({}, formToValidate, (_a = {}, _a[fieldName] = validateFormField(field), _a));
}
exports.validateOneFormField = validateOneFormField;
